import { Input, Modal, Form, Button, Progress, List, message } from "antd";
import { useState } from "react";
import { kolService } from "../services/kol.service";
import { PlusOutlined, CopyOutlined } from "@ant-design/icons";

interface AddKolsModalProps {
  onSuccess?: () => void;
}

interface KolStatus {
  username: string;
  status: "pending" | "success" | "error";
  error?: string;
}

const USERNAME_REGEX = /^[a-zA-Z0-9_]+$/;
const MIN_USERNAME_LENGTH = 1;
const MAX_USERNAME_LENGTH = 100;

export default function AddKolsModal({ onSuccess }: AddKolsModalProps) {
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [kolStatuses, setKolStatuses] = useState<KolStatus[]>([]);
  const [progress, setProgress] = useState(0);

  const validateUsernames = (
    usernames: string[]
  ): { valid: boolean; errors: string[] } => {
    const errors: string[] = [];
    const uniqueUsernames = new Set<string>();

    if (usernames.length === 0) {
      errors.push("Please enter at least one username");
      return { valid: false, errors };
    }

    usernames.forEach((username, index) => {
      const trimmedUsername = username.trim();

      if (!trimmedUsername) {
        errors.push(`Username at line ${index + 1} is empty`);
        return;
      }

      if (trimmedUsername.length < MIN_USERNAME_LENGTH) {
        errors.push(
          `Username "${trimmedUsername}" is too short (minimum ${MIN_USERNAME_LENGTH} characters)`
        );
      }

      if (trimmedUsername.length > MAX_USERNAME_LENGTH) {
        errors.push(
          `Username "${trimmedUsername}" is too long (maximum ${MAX_USERNAME_LENGTH} characters)`
        );
      }

      if (!USERNAME_REGEX.test(trimmedUsername)) {
        errors.push(
          `Username "${trimmedUsername}" contains invalid characters. Only letters, numbers, and underscores are allowed`
        );
      }

      if (uniqueUsernames.has(trimmedUsername)) {
        errors.push(`Username "${trimmedUsername}" is duplicated`);
      } else {
        uniqueUsernames.add(trimmedUsername);
      }
    });

    return { valid: errors.length === 0, errors };
  };

  const handleSubmit = async (values: { usernames: string }) => {
    const usernames = values.usernames
      .split("\n")
      .filter((username) => username.trim());

    const { valid, errors } = validateUsernames(usernames);

    if (!valid) {
      errors.forEach((error) => message.error(error));
      return;
    }

    setLoading(true);
    setKolStatuses(
      usernames.map((username) => ({ username, status: "pending" }))
    );
    setProgress(0);

    for (let i = 0; i < usernames.length; i++) {
      const username = usernames[i].trim();
      try {
        await kolService.addKols(username);
        setKolStatuses((prev) =>
          prev.map((status, index) =>
            index === i ? { ...status, status: "success" } : status
          )
        );
      } catch (error) {
        setKolStatuses((prev) =>
          prev.map((status, index) =>
            index === i
              ? { ...status, status: "error", error: "Failed to add KOL" }
              : status
          )
        );
        message.error(`Failed to add KOL: ${username}`);
      }
      setProgress(((i + 1) / usernames.length) * 100);
    }

    setLoading(false);
    if (progress === 100) {
      message.success("All KOLs added successfully");
      onSuccess?.();
    }
  };

  const reset = (shouldClose?: boolean) => {
    form.resetFields();
    setKolStatuses([]);
    setProgress(0);
    if (shouldClose) {
      setOpen(false);
    }
  };

  const handleCopyFailedUsernames = () => {
    const failedUsernames = kolStatuses
      .filter((status) => status.status === "error")
      .map((status) => status.username)
      .join("\n");

    if (failedUsernames) {
      navigator.clipboard.writeText(failedUsernames);
      message.success("Failed usernames copied to clipboard");
    } else {
      message.info("No failed usernames to copy");
    }
  };

  return (
    <>
      <Button
        icon={<PlusOutlined />}
        type="primary"
        onClick={() => setOpen(true)}
      >
        Add KOLs
      </Button>
      <Modal
        title="Add KOLs"
        open={open}
        onCancel={() => {
          if (kolStatuses.length === 0 || progress === 100) {
            reset(true);
          } else {
            message.warning("Please wait for the process to complete");
          }
        }}
        footer={null}
        width={600}
        closable={kolStatuses.length === 0 || progress === 100}
      >
        <Form form={form} onFinish={handleSubmit} layout="vertical">
          {kolStatuses.length === 0 && (
            <>
              <Form.Item
                label="KOL Usernames"
                name="usernames"
                rules={[
                  { required: true, message: "Please enter KOL usernames" },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      const usernames = value
                        .split("\n")
                        .filter((username: string) => username.trim());
                      const { valid, errors } = validateUsernames(usernames);
                      if (!valid) {
                        return Promise.reject(errors[0]);
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                help={`Enter one username per line. Usernames must be ${MIN_USERNAME_LENGTH}-${MAX_USERNAME_LENGTH} characters long and can only contain letters, numbers, and underscores.`}
              >
                <Input.TextArea
                  rows={10}
                  placeholder="Enter KOL usernames (one per line)"
                />
              </Form.Item>

              <div style={{ marginTop: 16, textAlign: "right" }}>
                <Button onClick={() => reset(true)} style={{ marginRight: 8 }}>
                  Cancel
                </Button>
                <Button type="primary" htmlType="submit" loading={loading}>
                  Add KOLs
                </Button>
              </div>
            </>
          )}

          {kolStatuses.length > 0 && (
            <>
              <Progress
                percent={progress}
                status={progress === 100 ? "success" : "active"}
                format={() =>
                  `${
                    kolStatuses.filter((s) => s.status !== "pending").length
                  }/${kolStatuses.length}`
                }
              />
              <List
                size="small"
                bordered
                dataSource={kolStatuses}
                renderItem={(item) => (
                  <List.Item>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        width: "100%",
                      }}
                    >
                      <span>{item.username}</span>
                      <span
                        style={{
                          color:
                            item.status === "success"
                              ? "green"
                              : item.status === "error"
                              ? "red"
                              : "orange",
                        }}
                      >
                        {item.status === "success"
                          ? "✓"
                          : item.status === "error"
                          ? "✗"
                          : "..."}
                      </span>
                    </div>
                  </List.Item>
                )}
              />

              <div style={{ marginTop: 16, textAlign: "right" }}>
                {kolStatuses.some((status) => status.status === "error") && (
                  <Button
                    icon={<CopyOutlined />}
                    onClick={handleCopyFailedUsernames}
                    style={{ marginRight: 8 }}
                  >
                    Copy Failed Usernames
                  </Button>
                )}
                <Button onClick={() => reset(true)} style={{ marginRight: 8 }}>
                  Close
                </Button>
                <Button
                  type="primary"
                  onClick={() => reset()}
                  disabled={kolStatuses.length === 0}
                >
                  Add More
                </Button>
              </div>
            </>
          )}
        </Form>
      </Modal>
    </>
  );
}
